import {
  PiAddressBook,
  PiCalendarBlank,
  PiCheck,
  PiEnvelopeSimple,
  PiEye,
  PiGear,
  PiLock,
  PiRocketLaunch,
  PiUsers,
} from 'react-icons/pi'
import { Link } from 'react-router-dom'

import { cn } from 'components/UI/cn'
import { TargetArrowOutlineIcon } from 'components/Layout/LeftNav/nav-icons'
import type { Campaign } from 'models/campaign'

export type WizardStep =
  | 'review-leads'
  | 'sequence'
  | 'settings'
  | 'review-emails'
  | 'launch'

interface StepDefinition {
  key: WizardStep
  label: string
  icon: typeof PiEnvelopeSimple
  requiresContacts?: boolean
}

export const ALL_STEPS: StepDefinition[] = [
  { key: 'review-leads', label: 'Contacts', icon: PiUsers },
  { key: 'sequence', label: 'Build', icon: PiEnvelopeSimple },
  { key: 'settings', label: 'Settings', icon: PiGear },
  {
    key: 'review-emails',
    label: 'Review',
    icon: PiEye,
    requiresContacts: true,
  },
  {
    key: 'launch',
    label: 'Launch',
    icon: PiRocketLaunch,
    requiresContacts: true,
  },
]

interface RecipientSummary {
  total: number
  included: number
  excluded: number
}

interface SequenceWizardSidebarProps {
  currentStep: WizardStep
  completedSteps: Set<WizardStep>
  accessibleSteps?: Set<WizardStep>
  campaign: Campaign | undefined
  campaignUrl?: string
  hasContacts: boolean
  onStepClick: (step: WizardStep) => void
  visibleStepsOverride?: WizardStep[]
  title?: string
  // Live recipient counts from the lead-review endpoint. When provided, the
  // "X recipients" line stays in sync with mutations during the build
  // (email changes, include/exclude, validation results) instead of the
  // denormalized campaign aggregate that only catches up around launch.
  recipientSummary?: RecipientSummary
}

export function SequenceWizardSidebar({
  currentStep,
  completedSteps,
  accessibleSteps,
  campaign,
  campaignUrl,
  hasContacts,
  onStepClick,
  visibleStepsOverride,
  title = 'Sequence Wizard',
  recipientSummary,
}: SequenceWizardSidebarProps) {
  const visibleSteps = visibleStepsOverride
    ? ALL_STEPS.filter((step) => visibleStepsOverride.includes(step.key))
    : hasContacts
      ? ALL_STEPS
      : ALL_STEPS.filter((s) => !s.requiresContacts)

  const lockedSteps = ALL_STEPS.filter((s) => s.requiresContacts)

  const formattedLaunchDate = campaign?.launch_date
    ? new Date(campaign.launch_date).toLocaleDateString('en-US', {
        month: 'short',
        day: 'numeric',
        year: 'numeric',
      })
    : null

  // Only render a number when we have the live included count. Falling back
  // to `campaign.contact_count` is wrong — that's the denormalized total, a
  // different number that lies whenever any contact is excluded (FIR-7242).
  const recipientLabel = (() => {
    if (!recipientSummary) return '—'
    const n = recipientSummary.included
    return n > 0 ? `${n} recipient${n === 1 ? '' : 's'}` : 'No recipients'
  })()

  const hasAnyContacts = (recipientSummary?.total ?? 0) > 0

  return (
    <div className="flex w-52 shrink-0 flex-col bg-white">
      {/* Campaign context */}
      <div className="flex h-28 flex-col justify-center px-4 pb-4">
        <p className="mb-1 text-sm font-medium text-fg-secondary">{title}</p>
        <h3 className="truncate text-sm font-medium">
          {campaignUrl ? (
            <Link
              to={campaignUrl}
              className="text-brand-bright hover:text-brand-active"
              title={`Back to ${campaign?.name ?? 'campaign'}`}
            >
              {campaign?.name ?? 'Untitled Campaign'}
            </Link>
          ) : (
            <span className="text-fg">
              {campaign?.name ?? 'Untitled Campaign'}
            </span>
          )}
        </h3>
        {formattedLaunchDate && (
          <p className="mt-1.5 flex items-center gap-1.5 text-xs text-fg-secondary">
            <PiCalendarBlank className="size-3" />
            Projected: {formattedLaunchDate}
          </p>
        )}
      </div>

      {/* Step navigation */}
      <nav className="flex-1 overflow-auto px-3 py-4">
        <div className="space-y-1">
          {visibleSteps.map((step) => {
            const isCompleted = completedSteps.has(step.key)
            const isCurrent = currentStep === step.key
            const isAccessible =
              accessibleSteps?.has(step.key) ?? (isCompleted || isCurrent)
            const StepIcon = step.icon

            return (
              <button
                type="button"
                key={step.key}
                onClick={() => onStepClick(step.key)}
                disabled={!isAccessible}
                className={cn(
                  'flex w-full items-center gap-3 rounded-lg px-3 py-2 text-left text-sm transition-all',
                  isCurrent && 'bg-brand-sage-100 text-brand-sage-700',
                  isCompleted &&
                    !isCurrent &&
                    'cursor-pointer text-fg hover:bg-brand-sage-50',
                  !isAccessible && 'cursor-default text-neutral-400'
                )}
              >
                <div
                  className={cn(
                    'flex size-7 shrink-0 items-center justify-center rounded-full transition-colors',
                    isCompleted && 'bg-brand-active text-white',
                    isCurrent &&
                      !isCompleted &&
                      'border-2 border-brand-sage-500 bg-brand-sage-100 text-brand-sage-700',
                    !isCompleted &&
                      !isCurrent &&
                      'bg-neutral-100 text-neutral-400'
                  )}
                >
                  {isCompleted ? (
                    <PiCheck className="size-3.5" />
                  ) : (
                    <StepIcon className="size-3.5" />
                  )}
                </div>
                <span className={cn(isCurrent && 'font-medium')}>
                  {step.label}
                </span>
              </button>
            )
          })}

          {/* Locked steps indicator when no contacts — hidden for live campaigns */}
          {!hasContacts && lockedSteps.length > 0 && !visibleStepsOverride && (
            <div className="mt-2 border-t border-dashed border-line px-3 pt-3">
              <div className="mb-2 flex items-center gap-2 text-xs text-neutral-400">
                <PiLock className="size-3" />
                <span>Requires contacts</span>
              </div>
              {lockedSteps.map((step) => {
                const StepIcon = step.icon
                return (
                  <div
                    key={step.key}
                    className="flex items-center gap-3 py-2 text-sm text-neutral-300"
                  >
                    <div className="flex size-7 shrink-0 items-center justify-center rounded-full bg-neutral-100">
                      <StepIcon className="size-3.5" />
                    </div>
                    <span>{step.label}</span>
                  </div>
                )
              })}
            </div>
          )}
        </div>
      </nav>

      {!hasContacts && !hasAnyContacts && (
        <p className="px-4 pt-3 text-sm leading-snug text-fg-secondary/70">
          Add contacts from the campaign page to unlock all steps.
        </p>
      )}

      {/* Campaign info footer */}
      <div className="flex h-24 flex-col justify-center px-4 py-3">
        <div className="mb-1 flex items-center gap-1 text-sm text-fg-secondary">
          <TargetArrowOutlineIcon className="size-3.5 shrink-0 text-brand-active" />
          <span>
            {(campaign?.contact_company_count ?? 0) > 0
              ? `${campaign?.contact_company_count} targets`
              : 'No targets'}
          </span>
        </div>
        <div className="flex items-center gap-1 text-sm text-fg-secondary">
          <PiAddressBook className="size-3.5 shrink-0 text-brand-active" />
          <span>{recipientLabel}</span>
        </div>
      </div>
    </div>
  )
}
